import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3, Link2, Undo2, Redo2, Minus,
} from "lucide-react";

/**
 * Rich text for investors and admins only. Members write plain text — a formatting
 * toolbar on a one-line comment box is noise, and every extra HTML surface is
 * another thing to sanitise.
 *
 * Output is HTML. It is sanitised **server-side** on the way in (see
 * `routes_site.sanitize_html`), because a client-side sanitiser only protects
 * people who go through the client. The toolbar is deliberately limited to the tags
 * that allow-list already accepts, so what you can type is what will survive.
 */
const BTN = "p-1.5 rounded-md transition-colors disabled:opacity-30";

function Tool({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`${BTN} ${active ? "bg-brand-100 text-brand-700" : "text-slate-500 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, placeholder = "", minHeight = 180, testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Headings above h3 would out-shout the page's own hierarchy.
        heading: { levels: [2, 3] },
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        // Matches the server's URL allow-list; anything else is stripped on save
        // anyway, so offering it here would just mislead.
        protocols: ["http", "https", "mailto"],
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  }, [placeholder]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-brand-500"
         data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50/70 px-2 py-1.5">
        <Tool testid="rt-bold" title="Bold" active={editor.isActive("bold")}
              onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Tool>
        <Tool testid="rt-italic" title="Italic" active={editor.isActive("italic")}
              onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Tool>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Tool testid="rt-h2" title="Heading" active={editor.isActive("heading", { level: 2 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Tool>
        <Tool testid="rt-h3" title="Subheading" active={editor.isActive("heading", { level: 3 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Tool>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Tool testid="rt-ul" title="Bullet list" active={editor.isActive("bulletList")}
              onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Tool>
        <Tool testid="rt-ol" title="Numbered list" active={editor.isActive("orderedList")}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Tool>
        <Tool testid="rt-quote" title="Quote" active={editor.isActive("blockquote")}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Tool>
        <Tool testid="rt-hr" title="Divider"
              onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={15} /></Tool>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Tool testid="rt-link" title="Link" active={editor.isActive("link")} onClick={addLink}><Link2 size={15} /></Tool>
        <div className="flex-1" />
        <Tool testid="rt-undo" title="Undo" disabled={!editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Tool>
        <Tool testid="rt-redo" title="Redo" disabled={!editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Tool>
      </div>
      <EditorContent
        editor={editor}
        className="legal-content px-4 py-3 outline-none"
        style={{ minHeight }}
      />
    </div>
  );
}
